import { computed, onUnmounted, ref } from 'vue'
import analizApi from '@/api/analiz.api'
import { resolveAnalysisInput } from '@/composables/gtfs/useGTFSFeedRegistry.js'

/** Aktif izokron transit altyapısı: null = üretim */
export const selectedNetworkStackId = ref(null)

const stacks = ref([])
const loading = ref(false)
const error = ref('')
const buildJobId = ref(null)
const buildStackId = ref(null)
const buildJob = ref(null)

let pollTimer = null
let notifyFn = null

const BUILD_PHASE_LABELS = {
  queued: 'Kuyrukta',
  merge: 'GTFS birleştiriliyor',
  staging: 'OTP dosyaları hazırlanıyor',
  building_graph: 'OTP graph build',
  activating: 'Önizleme aktifleştiriliyor',
  done: 'Tamamlandı',
  failed: 'Başarısız',
}

function _notify(type, message, duration) {
  if (typeof notifyFn === 'function') {
    notifyFn(type, message, duration)
  }
}

async function refreshStacks() {
  loading.value = true
  error.value = ''
  try {
    const res = await analizApi.listNetworkStacks()
    if (res?.success) {
      stacks.value = res.items || []
      const building = (res.drafts || []).find((s) =>
        ['building', 'staging', 'building_graph'].includes(s.status)
      )
      if (building?.job_id && !buildJobId.value) {
        pollBuildJob(building.job_id, building.id)
      }
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Stack listesi alınamadı'
    _notify('error', error.value)
  } finally {
    loading.value = false
  }
}

function stopPoll() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function fetchBuildJob(jobId) {
  try {
    const res = await analizApi.getJob(jobId)
    const job = res?.job || res
    buildJob.value = job || null
    return job
  } catch {
    return null
  }
}

async function pollBuildJob(jobId, stackId) {
  buildJobId.value = jobId
  buildStackId.value = stackId
  stopPoll()
  await fetchBuildJob(jobId)
  pollTimer = setInterval(async () => {
    const job = await fetchBuildJob(jobId)
    if (!job) return
    const status = job.status
    if (status === 'success' || status === 'failed') {
      stopPoll()
      buildJobId.value = null
      buildStackId.value = null
      await refreshStacks()
      if (status === 'success' && stackId) {
        selectedNetworkStackId.value = stackId
        _notify(
          'success',
          'OTP önizleme altyapısı hazır. Listeden seçip izokron deneyebilirsiniz.',
          6000
        )
      }
      if (status === 'failed') {
        const msg = job.error_message || 'Altyapı build başarısız'
        error.value = msg
        _notify('error', msg, 8000)
      }
    }
  }, 3000)
}

async function buildStackFromEditor({ label, serviceDate } = {}) {
  const input = await resolveAnalysisInput()
  if (!input?.success) {
    throw new Error(input?.error || 'Analiz girdisi hazırlanamadı')
  }

  const fields = {
    label,
    service_date: serviceDate,
    feed_count: input.feedCount,
  }

  let res
  if (input.mode === 'feed_ids') {
    res = await analizApi.buildNetworkStack({
      feed_ids: input.feed_ids,
      ...fields,
    })
  } else if (input.mode === 'zips' && input.files?.length) {
    res = await analizApi.buildNetworkStackMultipart(input.files, fields)
  } else if (input.mode === 'tables' && input.tables) {
    res = await analizApi.buildNetworkStack({
      tables: input.tables,
      ...fields,
    })
  } else {
    throw new Error('Analize dahil feed bulunamadı')
  }

  if (!res?.success) {
    throw new Error(res?.error || 'Build başlatılamadı')
  }

  _notify(
    'info',
    'OTP build başlatıldı. İlerleme bu panelde görüntülenir (15–45 dk).',
    7000
  )

  await refreshStacks()
  if (res.job_id) {
    pollBuildJob(res.job_id, res.stack_id)
  }
  return res
}

/** @deprecated — buildStackFromEditor kullanın */
async function buildStack({ feedIds, label, serviceDate }) {
  if (!feedIds?.length) {
    return buildStackFromEditor({ label, serviceDate })
  }
  const res = await analizApi.buildNetworkStack({
    feed_ids: feedIds,
    label,
    service_date: serviceDate,
  })
  if (!res?.success) {
    throw new Error(res?.error || 'Build başlatılamadı')
  }
  _notify('info', 'OTP build başlatıldı.', 5000)
  await refreshStacks()
  if (res.job_id) {
    pollBuildJob(res.job_id, res.stack_id)
  }
  return res
}

async function promoteStack(stackId) {
  const res = await analizApi.promoteNetworkStack(stackId)
  if (!res?.success) {
    throw new Error(res?.error || 'Üretime alınamadı')
  }
  selectedNetworkStackId.value = null
  _notify('success', 'Üretim OTP graph güncellendi.', 5000)
  await refreshStacks()
  return res
}

async function discardStack(stackId) {
  const res = await analizApi.deleteNetworkStack(stackId)
  if (!res?.success) {
    throw new Error(res?.error || 'Taslak silinemedi')
  }
  if (selectedNetworkStackId.value === stackId) {
    selectedNetworkStackId.value = null
  }
  _notify('info', 'Taslak altyapı silindi.', 4000)
  await refreshStacks()
  return res
}

export function useNetworkStacks(opts = {}) {
  if (typeof opts.notify === 'function') {
    notifyFn = opts.notify
  }

  const production = computed(
    () => stacks.value.find((s) => s.role === 'production') || null
  )
  const drafts = computed(() =>
    (stacks.value || []).filter((s) => s.role === 'draft')
  )
  const readyDrafts = computed(() =>
    drafts.value.filter((s) => s.status === 'ready')
  )
  const failedDrafts = computed(() =>
    drafts.value.filter((s) => s.status === 'failed')
  )
  const buildingDraft = computed(() =>
    drafts.value.find((s) =>
      ['building', 'staging', 'building_graph'].includes(s.status)
    )
  )
  const selectedStack = computed(() => {
    const id = selectedNetworkStackId.value
    if (!id) return production.value
    return stacks.value.find((s) => Number(s.id) === Number(id)) || null
  })

  const buildProgressPct = computed(() => {
    const job = buildJob.value
    if (!job) return 0
    const total = Number(job.progress_total) || 5
    const done = Number(job.progress_done) || 0
    if (total <= 0) return 0
    return Math.min(100, Math.round((100 * done) / total))
  })

  const buildPhaseLabel = computed(() => {
    const job = buildJob.value
    const params = job?.params || {}
    if (params.phase_label) return params.phase_label
    const phase = params.phase || job?.status
    return BUILD_PHASE_LABELS[phase] || phase || 'İşleniyor…'
  })

  const buildLogLines = computed(() => {
    const logs = buildJob.value?.params?.logs
    return Array.isArray(logs) ? logs.slice(-6) : []
  })

  const isBuildRunning = computed(
    () => !!buildJobId.value || !!buildingDraft.value
  )

  onUnmounted(() => stopPoll())

  return {
    stacks,
    production,
    drafts,
    readyDrafts,
    failedDrafts,
    buildingDraft,
    selectedStack,
    selectedNetworkStackId,
    loading,
    error,
    buildJobId,
    buildStackId,
    buildJob,
    buildProgressPct,
    buildPhaseLabel,
    buildLogLines,
    isBuildRunning,
    refreshStacks,
    buildStack,
    buildStackFromEditor,
    promoteStack,
    discardStack,
    stopPoll,
  }
}
